import {
    FeedbackRequestWithDetails,
    FeedbackWithDetails,
} from '@/common/types/databaseTypes';
import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Card,
    Group,
    Menu,
    Paper,
    Stack,
    Text,
} from '@mantine/core';
import {
    IconChevronDown,
    IconChevronUp,
    IconDots,
    IconNotesOff,
    IconSend,
    IconStarFilled,
} from '@tabler/icons-react';
import { CoreValueBadge } from '../components/CoreValueBadge';
import UserAvatar from '@/common/components/UserAvatars/UserAvatar';
import { Icon, ICONS } from '@/common/components/Icon';
import { FeedbackCommentForm } from '../components/FeedbackCommentForm';
import TodoBadgeList from '../components/TodoBadgeList';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import useFeedbackModalStore from '../hooks/useFeedbackModalStore';
import { useStackModalStore } from '@/common/stores/modalStore';
import {
    useDeleteFeedbackRequest,
    useHideFeedbackForUser,
    useRejectFeedbackRequest,
    useToggleFeedbackBookmark,
} from '../services/useFeedbackQueries';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { CommentList } from '@/features/comment/components/CommentList';
import { formatDate } from '@tickitacka/shared';
import { EmojiReactionGroup } from '@/features/emoji_reaction/components/EmojiReactionGroup';

interface FeedbackCardProps {
    feedback: FeedbackWithDetails;
    isReceived: boolean;
}

interface FeedbackRequestCardProps {
    request: FeedbackRequestWithDetails;
    isReceived: boolean;
    notiParams: {
        workspaceName: string;
        workspaceImageUrl: string;
    };
}

/**
 * 피드백 카드 렌더링 함수
 * @param feedback 피드백 정보
 * @param isReceived 받은 피드백 여부
 */
export const FeedbackCard = ({ feedback, isReceived }: FeedbackCardProps) => {
    const { t } = useTranslation(['common', 'feedback']);
    const { user } = useAuthStore.getState();
    // const displayUser = isReceived ? feedback.sender : feedback.receiver;
    const [isHovered, setIsHovered] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isRequestOpen, setIsRequestOpen] = useState(false);

    const { openModal } = useFeedbackModalStore();

    const sender = isReceived ? feedback.sender : user;
    const receiver = isReceived ? user : feedback.receiver;

    const deleteFeedback = useHideFeedbackForUser();
    const toggleFeedbackBookmark = useToggleFeedbackBookmark();

    // 피드백 삭제 핸들러 (나에게서만 삭제)
    const handleDeleteFeedback = async () => {
        const notificationId = notifications.show({
            title: t('feedback:deleteFeedback.title'),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        try {
            await deleteFeedback.mutateAsync(feedback);
            notifications.update({
                id: notificationId,
                title: t('feedback:deleteFeedback.title'),
                message: t('feedback:deleteFeedback.success'),
                loading: false,
                autoClose: 3000,
                color: 'green',
            });
        } catch (e) {
            notifications.update({
                id: notificationId,
                title: t('feedback:deleteFeedback.title'),
                message: t('feedback:deleteFeedback.error'),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        }
    };

    // 피드백 삭제 모달 오픈
    const handleDeleteFeedbackClick = () => {
        const modalId = useStackModalStore
            .getState()
            .openModal('CONFIRMATION', {
                title: t('feedback:deleteFeedback.title'),
                message: isReceived
                    ? t('feedback:deleteFeedback.receiver_message')
                    : t('feedback:deleteFeedback.sender_message'),
                onConfirm: () => {
                    useStackModalStore.getState().closeModal(modalId);
                    void handleDeleteFeedback();
                },
                confirmText: t('action.delete'),
                cancelText: t('button.cancel'),
                confirmButtonColor: 'red',
            });
    };

    // 북마크 토글 처리
    const handleToggleFeedbackBookmark = async (isBookmarked: boolean) => {
        const notificationId = notifications.show({
            title: isBookmarked
                ? t('feedback:bookmark.remove.title')
                : t('feedback:bookmark.add.title'),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        try {
            await toggleFeedbackBookmark.mutateAsync({
                feedback,
                isBookmarked: !isBookmarked,
            });
            notifications.update({
                id: notificationId,
                title: isBookmarked
                    ? t('feedback:bookmark.remove.title')
                    : t('feedback:bookmark.add.title'),
                message: isBookmarked
                    ? t('feedback:bookmark.remove.success')
                    : t('feedback:bookmark.add.success'),
                loading: false,
                autoClose: 3000,
                color: 'green',
            });
        } catch (e) {
            notifications.update({
                id: notificationId,
                title: isBookmarked
                    ? t('feedback:bookmark.remove.title')
                    : t('feedback:bookmark.add.title'),
                message: t('feedback:bookmark.error'),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        }
    };

    // 북마크 토클 모달 오픈
    const handleToggleBookmarkClick = () => {
        const isBookmarked = feedback.is_bookmarked;

        const modalId = useStackModalStore
            .getState()
            .openModal('CONFIRMATION', {
                title: isBookmarked
                    ? t('feedback:bookmark.remove.title')
                    : t('feedback:bookmark.add.title'),
                message: isBookmarked
                    ? t('feedback:bookmark.remove.message')
                    : t('feedback:bookmark.add.message'),
                onConfirm: () => {
                    useStackModalStore.getState().closeModal(modalId);

                    void handleToggleFeedbackBookmark(isBookmarked);
                },
            });
    };

    return (
        <Card
            key={feedback.id}
            id={`feedback-${feedback.id}`}
            withBorder
            px={56}
            py="xl"
            radius="lg"
            mb="lg"
            shadow="none"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => !isMenuOpen && setIsHovered(false)}
        >
            {!feedback.is_read && isReceived && (
                <Badge color="orange" mb={12}>
                    New
                </Badge>
            )}

            {feedback.is_bookmarked && (
                <Group gap={4} c="dimmed" mb={16}>
                    <IconStarFilled size={14} />
                    <Text size="sm">{t('feedback:bookmark.title')}</Text>
                </Group>
            )}

            <Group justify="space-between" mb="md">
                {/* 보낸 사람, 받은 사람 */}
                <Group gap={12} py={2}>
                    <Group gap={8}>
                        <UserAvatar
                            name={sender?.name}
                            imageUrl={sender?.profile_image_url}
                            size={20}
                        />
                        <Box>
                            <Text fw={600}>{sender?.name}</Text>
                        </Box>
                    </Group>
                    <Icon name={ICONS.ARROW.FEEDBACK_ARROW} />
                    <Group gap={8}>
                        <UserAvatar
                            name={receiver?.name}
                            imageUrl={receiver?.profile_image_url}
                            size={20}
                        />
                        <Box>
                            <Text fw={600}>{receiver?.name}</Text>
                        </Box>
                    </Group>
                    {feedback.request && (
                        <Button
                            variant="light"
                            size="xs"
                            radius="md"
                            rightSection={
                                isRequestOpen ? (
                                    <IconChevronUp size={12} />
                                ) : (
                                    <IconChevronDown size={12} />
                                )
                            }
                            onClick={() => setIsRequestOpen(!isRequestOpen)}
                        >
                            <Text size="xs">
                                {t('feedback:feedbackCard.seeRequest')}
                            </Text>
                        </Button>
                    )}
                </Group>

                <Group gap="xs">
                    {/* 핵심 가치 */}
                    {feedback.feedback_core_values &&
                        feedback.feedback_core_values.length > 0 && (
                            <Group gap="xs">
                                {feedback.feedback_core_values
                                    .sort((a, b) =>
                                        a.is_positive === b.is_positive
                                            ? 0
                                            : a.is_positive
                                              ? -1
                                              : 1
                                    )
                                    .map(cv => (
                                        <CoreValueBadge
                                            key={cv.core_value.id}
                                            label={cv.core_value.name}
                                            colorName={
                                                cv.is_positive ? 'blue' : 'red'
                                            }
                                            withIcon
                                        />
                                    ))}
                            </Group>
                        )}

                    {/* 더보기 버튼 */}
                    {
                        // (isHovered || isMenuOpen) &&
                        <Menu
                            shadow="md"
                            position="bottom-end"
                            onChange={opened => {
                                setIsMenuOpen(opened);
                            }}
                        >
                            <Menu.Target>
                                <ActionIcon
                                    size="md"
                                    radius="md"
                                    variant="subtle"
                                    color="gray"
                                >
                                    <IconDots size={16} />
                                </ActionIcon>
                            </Menu.Target>

                            <Menu.Dropdown>
                                {!isReceived && (
                                    <Menu.Item
                                        onClick={() =>
                                            openModal('edit-feedback', {
                                                data: feedback,
                                            })
                                        }
                                    >
                                        {t('feedback:action.edit')}
                                    </Menu.Item>
                                )}
                                {isReceived && (
                                    <Menu.Item
                                        onClick={handleToggleBookmarkClick}
                                    >
                                        {feedback.is_bookmarked
                                            ? t(
                                                  'feedback:action.removeBookmark'
                                              )
                                            : t('feedback:action.addBookmark')}
                                    </Menu.Item>
                                )}
                                <Menu.Item
                                    color="red"
                                    onClick={handleDeleteFeedbackClick}
                                >
                                    {t('feedback:action.delete')}
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    }
                </Group>
            </Group>

            {/* 요청 확인 */}
            {isRequestOpen && (
                <Paper p="md" mb={8} radius="md" bg="gray.0">
                    <Stack gap={8} align="start" fz="sm">
                        <Text size="sm" fw={600}>
                            {t('feedback:feedbackCard.requestTitle')}
                        </Text>
                        <Text size="sm">{feedback.request.content}</Text>
                    </Stack>
                </Paper>
            )}

            {/* 투두 목록 */}
            <TodoBadgeList feedbackTodos={feedback.feedback_todos} />

            {/* 피드백 내용 */}
            <Text
                size="sm"
                mb={8}
                style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {feedback.content}
            </Text>

            <Group gap={4} mb="md" c="dimmed">
                <Text size="xs">
                    {formatDate(new Date(feedback.register_date), {
                        showWeekday: false,
                        shortenMonth: true,
                    })}
                </Text>
                {/* 2분 이상 차이나면 수정됨 표시 */}
                {new Date(feedback.update_date).getTime() -
                    new Date(feedback.register_date).getTime() >
                    2 * 60 * 1000 && (
                    <Text size="xs">({t('action.edited')})</Text>
                )}
            </Group>

            <EmojiReactionGroup
                entityType="feedback"
                entityId={feedback.id}
                editable={isReceived}
                emojiReactions={feedback.emoji_reactions}
                notificationConfig={{
                    notificationReceiverId: isReceived
                        ? feedback.sender?.id
                        : feedback.receiver?.id,
                    feedbackReceiverId: feedback.receiver?.id,
                    feedbackContent: feedback.content,
                    workspaceId: feedback.workspace_id,
                }}
            />

            {/* 댓글 인풋 */}
            <Box mb={feedback?.comments?.length === 0 ? 0 : 'md'}>
                <FeedbackCommentForm
                    targetId={feedback.id}
                    // workspaceId={feedback.workspace_id}
                />
            </Box>

            {feedback.comments && feedback.comments.length > 0 && (
                <CommentList
                    comments={feedback.comments}
                    targetId={feedback.id}
                    targetType="feedback"
                />
            )}
        </Card>
    );
};

/**
 * 피드백 요청 카드 렌더링 함수
 * @param request 피드백 요청 정보
 * @param isReceived 받은 요청 여부
 */
export const FeedbackRequestCard = ({
    request,
    isReceived,
    notiParams,
}: FeedbackRequestCardProps) => {
    const { t } = useTranslation(['common', 'feedback']);
    const displayUser = isReceived ? request.sender : request.receiver;
    const { openModal } = useFeedbackModalStore();

    const deleteFeedbackRequest = useDeleteFeedbackRequest();
    const rejectFeedbackRequest = useRejectFeedbackRequest();

    const handleRejectRequest = async () => {
        const notificationId = notifications.show({
            title: t('feedback:rejectFeedback.title'),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        try {
            await rejectFeedbackRequest.mutateAsync({
                feedbackRequest: request,
                notiParams,
            });

            notifications.update({
                id: notificationId,
                title: t('feedback:rejectFeedback.title'),
                message: t('feedback:rejectFeedback.success'),
                loading: false,
                autoClose: 3000,
                color: 'green',
            });
        } catch (e) {
            console.error('피드백 요청 거부 처리 중 오류 발생', e);
            notifications.update({
                id: notificationId,
                title: t('feedback:rejectFeedback.title'),
                message: t('feedback:rejectFeedback.error'),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        }
    };

    const handleDeleteRequest = async () => {
        const isRejected = request.is_rejected; // 리젝된 경우 '삭제'로, 아니면 '취소'로 표시해줌
        const notiTitle = isRejected
            ? t('feedback:deleteRequest.title')
            : t('feedback:cancelRequest.title');

        const notificationId = notifications.show({
            title: notiTitle,
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        try {
            await deleteFeedbackRequest.mutateAsync(request);

            notifications.update({
                id: notificationId,
                title: notiTitle,
                message: isRejected
                    ? t('feedback:deleteRequest.success')
                    : t('feedback:cancelRequest.success'),
                loading: false,
                autoClose: 3000,
                color: 'green',
            });
        } catch (e) {
            console.error('피드백 요청 삭제 중 오류 발생', e);
            notifications.update({
                id: notificationId,
                title: notiTitle,
                message: isRejected
                    ? t('feedback:deleteRequest.error')
                    : t('feedback:cancelRequest.error'),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        }
    };

    // 피드백 요청 삭제 핸들러
    const handleDeleteFeedbackRequest = () => {
        const modalId = useStackModalStore
            .getState()
            .openModal('CONFIRMATION', {
                title: isReceived
                    ? t('feedback:rejectFeedback.title')
                    : request.is_rejected
                      ? t('feedback:deleteRequest.title')
                      : t('feedback:cancelRequest.title'),
                message: isReceived
                    ? t('feedback:rejectFeedback.message', {
                          name: displayUser?.name,
                      })
                    : request.is_rejected
                      ? t('feedback:deleteRequest.message')
                      : t('feedback:cancelRequest.message'),
                onConfirm: () => {
                    useStackModalStore.getState().closeModal(modalId);

                    if (isReceived) {
                        void handleRejectRequest();
                    } else {
                        void handleDeleteRequest();
                    }
                },
                confirmText: isReceived
                    ? t('action.reject')
                    : t('button.confirm'),
                cancelText: t('button.cancel'),
                confirmButtonColor: 'red',
            });
    };

    return (
        <Card
            key={request.id}
            id={`request-${request.id}`}
            withBorder
            px="xl"
            py="lg"
            radius="lg"
            mb="lg"
            shadow="none"
            bg={request.is_rejected ? 'red.0' : 'transparent'}
        >
            <Group justify="space-between" mb="md">
                <Group gap="xs">
                    <UserAvatar
                        name={displayUser?.name}
                        imageUrl={displayUser?.profile_image_url}
                        size={20}
                    />
                    <Box>
                        <Text fw={600}>{displayUser?.name}</Text>
                    </Box>
                </Group>

                <Group gap="xs">
                    {request.is_rejected && (
                        <Box c="red" px={8}>
                            <Group gap={4}>
                                <IconNotesOff size={16} />
                                <Text size="sm">{t('action.rejected')}</Text>
                            </Group>
                        </Box>
                    )}

                    <Button
                        color="gray"
                        variant="default"
                        radius={8}
                        px="md"
                        onClick={handleDeleteFeedbackRequest}
                    >
                        {isReceived
                            ? t('action.reject')
                            : request.is_rejected
                              ? t('action.delete')
                              : t('feedback:action.cancelRequest')}
                    </Button>

                    {isReceived ? (
                        <Button
                            leftSection={<IconSend size={16} />}
                            radius={8}
                            onClick={() =>
                                openModal('respond', { data: request })
                            }
                        >
                            {t('feedback:action.sendFeedback')}
                        </Button>
                    ) : (
                        <Button
                            color="gray"
                            radius={8}
                            onClick={() =>
                                openModal('edit-request', { data: request })
                            }
                        >
                            {request.is_rejected
                                ? t('feedback:action.resend')
                                : t('feedback:action.editRequest')}
                        </Button>
                    )}
                </Group>
            </Group>

            {/* todo */}
            <TodoBadgeList feedbackTodos={request.feedback_request_todos} />

            <Text
                mb="md"
                size="sm"
                style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {request.content}
            </Text>
        </Card>
    );
};
